import React from 'react';
import { Icons } from './Icons';

// Single shimmer block — uses the .skeleton class from index.css
const Sk = ({ className = '' }) => <div className={`skeleton ${className}`} />;
const SkLight = ({ className = '' }) => <div className={`skeleton-light ${className}`} />;

// ── Dashboard Skeleton (admin & user) ─────────────────────────────────────
export const DashboardSkeleton = () => (
  <div className="space-y-6">
    {/* Stat cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-[16px] border border-gray-200 p-5 space-y-3">
          <Sk className="h-3 w-20" />
          <Sk className="h-7 w-12" />
          <Sk className="h-2 w-16" />
        </div>
      ))}
    </div>

    {/* Charts row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-[20px] border border-gray-200 p-6 space-y-3">
        <Sk className="h-4 w-40" />
        <Sk className="h-[180px] w-full" />
      </div>
      <div className="bg-white rounded-[20px] border border-gray-200 p-6 flex flex-col items-center space-y-3">
        <Sk className="h-4 w-32 self-start" />
        <Sk className="h-[160px] w-[160px] rounded-full" />
      </div>
    </div>

    {/* Table */}
    <TableSkeleton rows={5} cols={5} />
  </div>
);

// ── Mobile Dashboard Skeleton (for user mobile view) ──────────────────────
export const MobileDashboardSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-b from-[#d1f9ec] via-[#f0fdf9] to-[#f4f7f6] p-5 pt-8 space-y-6 pb-32">
    {/* Greetings & Date */}
    <div className="space-y-2">
      <Sk className="h-6 w-48" />
      <div className="flex justify-between items-center">
        <Sk className="h-3 w-36" />
        <Sk className="h-6 w-28 rounded-full" />
      </div>
    </div>

    {/* Compact Countdown card skeleton */}
    <div className="bg-[#0D3B2E] rounded-[14px] p-3.5 text-white shadow-md space-y-2.5">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <SkLight className="h-2 w-28 rounded" />
          <SkLight className="h-4 w-36 rounded" />
        </div>
        <SkLight className="h-5 w-24 rounded-full" />
      </div>
      <div className="pt-2.5 border-t border-white/10 grid grid-cols-4 gap-2">
        {[...Array(4)].map((_, idx) => (
          <div key={idx} className="bg-[#135744] h-10 rounded-lg py-1 px-1 flex flex-col items-center justify-center">


          </div>
        ))}
      </div>
    </div>

    {/* Stat cards 2x2 grid */}
    <div className="grid grid-cols-2 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-[16px] p-4 shadow-sm border border-gray-100/50 space-y-3 h-[130px]">
          <div className="flex justify-between items-start">
            <Sk className="h-3 w-16" />
            <Sk className="h-4 w-4 rounded-full" />
          </div>
          <Sk className="h-8 w-12" />
          <Sk className="h-2 w-full" />
        </div>
      ))}
    </div>

    {/* Recent submissions mobile card */}
    <div className="bg-white rounded-[16px] shadow-sm overflow-hidden border border-gray-100">
      <div className="bg-[#054334] px-4 py-3 flex justify-between items-center">
        <Sk className="h-4 w-40" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
        <Sk className="h-3 w-16" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
      </div>
      <div className="divide-y divide-gray-100 p-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="px-4 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-2 w-1/2">
              <Sk className="h-4 w-8 rounded" />
              <Sk className="h-3 w-24" />
            </div>
            <div className="flex w-1/2 justify-between items-center">
              <Sk className="h-3 w-12" />
              <Sk className="h-4 w-14 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Compliance progress mobile card */}
    <div className="bg-white rounded-[16px] shadow-sm p-4 border border-gray-100 space-y-3">
      <div className="flex justify-between items-center">
        <Sk className="h-4 w-36" />
        <Sk className="h-4 w-12" />
      </div>
      <Sk className="h-2 w-full rounded-full" />
      <div className="space-y-2 pt-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-2">
            <Sk className="h-4 w-4 rounded-full" />
            <Sk className="h-3 w-40" />
          </div>
        ))}
      </div>
    </div>

    {/* Announcements mobile card */}
    <div className="bg-white rounded-[16px] shadow-sm p-4 border border-gray-100 space-y-3">
      <Sk className="h-4 w-32" />
      <Sk className="h-24 w-full rounded-xl" />
      <Sk className="h-3 w-3/4" />
    </div>
  </div>
);

// ── User Dashboard Skeleton (for user home page desktop view) ───────────────
export const UserDashboardSkeleton = ({ isOffCampus = false }) => (
  <div className="space-y-6">
    {/* Header Area */}
    <div className="flex justify-between items-end mb-6">
      <div className="space-y-2">
        <Sk className="h-9 w-64 md:w-80 rounded-lg" />
        <Sk className="h-4 w-48 rounded" />
      </div>
    </div>

    {/* Countdown Lock Date Card (only for accreditation/re-accreditation) */}
    {!isOffCampus && (
      <div className="bg-[#0D3B2E] rounded-[10px] p-5 text-white shadow-lg relative overflow-hidden flex flex-col md:flex-row md:items-center w-fit mb-6 pr-12">
        <div className="z-10 mr-12 mb-4 md:mb-0 ml-5 space-y-2">
          <SkLight className="h-2.5 w-48 rounded" />
          <SkLight className="h-8 w-56 rounded-lg" />
        </div>

        <div className="flex space-x-3 z-10">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <div className="bg-[#135744] rounded-lg w-14 h-16 flex items-center justify-center">
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Stat Cards — 4 columns matching Pending / Reviewed / For Revision / Rejected */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      {[
        { titleW: 'w-16', color: 'bg-yellow-400' },
        { titleW: 'w-20', color: 'bg-green-500' },
        { titleW: 'w-24', color: 'bg-orange-500' },
        { titleW: 'w-16', color: 'bg-red-500' },
      ].map((card, i) => (
        <div
          key={i}
          className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px]"
        >
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px]">
            <div>
              <Sk className={`h-4 ${card.titleW} rounded`} />
            </div>
            <div className="flex-1 flex items-center">
              <Sk className="h-16 w-14 rounded-lg" />
            </div>
            <div className="mt-auto space-y-2">
              <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
              </div>
              <div className="flex justify-between">
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Main Grid: Left Column (Recent Submissions) + Right Column (Compliance Progress & Announcements) */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column (Submissions Table) */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-[#054334] px-6 py-4 flex justify-between items-center">
            <SkLight className="h-4 w-56 rounded" />
            <SkLight className="h-3 w-16 rounded" />
          </div>

          {/* Table Header */}
          <div className="overflow-x-auto">
            <div className="min-w-[500px]">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="col-span-6">
                  <Sk className="h-2.5 w-20 rounded" />
                </div>
                <div className="col-span-3">
                  <Sk className="h-2.5 w-12 rounded" />
                </div>
                <div className="col-span-3">
                  <Sk className="h-2.5 w-14 rounded" />
                </div>
              </div>

              {/* Document rows */}
              <div className="divide-y divide-gray-100">
                {[...Array(3)].map((_, idx) => (
                  <div
                    key={idx}
                    className={`grid grid-cols-12 gap-4 px-6 py-4 items-center ${idx % 2 === 0 ? 'bg-[#f8fafc]' : ''}`}
                  >
                    <div className="col-span-6 flex items-center space-x-3">
                      <div className="w-8 h-5 rounded bg-gray-200 border border-gray-200 flex items-center justify-center flex-shrink-0">
                      </div>
                      <Sk className="h-3.5 w-48 rounded" />
                    </div>
                    <div className="col-span-3">
                      <Sk className="h-3 w-16 rounded" />
                    </div>
                    <div className="col-span-3 flex justify-between items-center">
                      <Sk className="h-5 w-16 rounded-md" />
                      <div className="w-12 h-6 border border-gray-200 rounded flex items-center justify-center">

                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column (Widgets) */}
      <div className="space-y-6">
        {/* Compliance Progress Widget */}
        <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden flex flex-col">

          <div className="p-5 flex-1 flex flex-col space-y-4">
            <div>
              <div className="flex justify-between items-end mb-2">
                <Sk className="h-3 w-28 rounded" />
                <Sk className="h-3 w-8 rounded" />
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">

              </div>
            </div>

            {/* Checklist items */}
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  </div>
                  <Sk className="h-3 w-40 rounded" />
                </div>
              ))}
            </div>

            {/* View Documents Button */}
            <div className="w-full h-10 bg-[#054334] rounded-xl flex items-center justify-center">
            </div>
          </div>
        </div>

        {/* Announcements Widget Skeleton */}
        <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 p-5 space-y-3">
          <div className="flex justify-between items-center">
            <Sk className="h-4 w-28 rounded" />
            <Sk className="h-3 w-14 rounded" />
          </div>
          <Sk className="h-24 w-full rounded-xl" />
        </div>
      </div>
    </div>
  </div>
);


// ── Table Skeleton ────────────────────────────────────────────────────────
export const TableSkeleton = ({ rows = 6, cols = 4, title = true }) => (
  <div className="bg-white rounded-[12px] border border-gray-200 overflow-hidden shadow-sm">
    {title && (
      <div className="bg-[#086C53] px-6 py-4">
        <Sk className="h-4 w-48" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
      </div>
    )}
    <div className="overflow-x-auto">
      <div className="min-w-[480px]">
        {/* Col headers */}
        <div className="flex gap-4 px-6 py-3 bg-gray-50 border-b border-gray-100">
          {[...Array(cols)].map((_, i) => (
            <Sk key={i} className="h-2.5 flex-1" />
          ))}
        </div>
        {/* Rows */}
        {[...Array(rows)].map((_, ri) => (
          <div key={ri} className="flex gap-4 px-6 py-4 border-b border-gray-100 last:border-0">
            {[...Array(cols)].map((_, ci) => (
              <Sk key={ci} className={`h-4 flex-1 ${ci === 0 ? 'max-w-[80px]' : ''}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Admin Accreditation Table Skeleton ────────────────────────────────────
export const AdminAccreditationSkeleton = ({
  title = "Accreditation Applications",
  subtitle = "Review and process organization accreditation requirements.",
  rows = 6,
}) => (
  <div className="space-y-6 relative pb-16">
    {/* Page Title & Subtitle */}
    <div>
      <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="text-gray-500 text-sm mt-1">
          {subtitle}
        </p>
      )}
    </div>

    {/* Controls: Search, Filter, Select */}
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
      <div className="flex items-center space-x-3 w-full md:w-auto flex-wrap gap-y-2">
        {/* Search input mock */}
        <div className="relative w-full md:w-[400px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Icons.Search />
          </div>
          <div className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 bg-white text-sm font-medium flex items-center h-[42px]">
            <span className="text-gray-400 select-none">Search by organization name...</span>
          </div>
        </div>

        {/* Filter button mock */}
        <div className="bg-white border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg text-sm font-medium flex items-center space-x-2 h-[42px] select-none">
          <Icons.Filter />
          <span>Filter</span>
        </div>

        {/* Select button mock */}
        <div className="bg-white border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg text-sm font-medium flex items-center space-x-2 h-[42px] select-none">
          <Icons.Select />
          <span>Select</span>
        </div>
      </div>
    </div>

    {/* Table Card */}
    <div className="bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse table-fixed min-w-[760px]">
          <thead>
            <tr className="bg-[#086C53] text-white">
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider rounded-tl-[12px] w-[24%] min-w-[170px]">
                Organization
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[22%] min-w-[150px]">
                Representative
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[15%] min-w-[120px]">
                College/Campus
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[17%] min-w-[130px]">
                Completion
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[13%] min-w-[110px]">
                Status
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right rounded-tr-[12px] w-[9%] min-w-[80px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[...Array(rows)].map((_, i) => {
              const orgWidths = ['w-12', 'w-16', 'w-10', 'w-14', 'w-16', 'w-20'];
              const repWidths = ['w-28', 'w-36', 'w-32', 'w-24', 'w-30', 'w-34'];
              const emailWidths = ['w-32', 'w-40', 'w-36', 'w-28', 'w-36', 'w-32'];
              const collegeWidths = ['w-10', 'w-12', 'w-14', 'w-10', 'w-12', 'w-10'];
              const progressWidths = ['w-full', 'w-1/4', 'w-4/5', 'w-1/3', 'w-2/3', 'w-1/2'];
              const badgeWidths = ['w-24', 'w-28', 'w-24', 'w-24', 'w-28', 'w-24'];

              return (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  {/* Organization */}
                  <td className="px-6 py-4">
                    <Sk className={`h-4 ${orgWidths[i % orgWidths.length]} rounded mb-1.5`} />
                    <Sk className={`h-4 ${badgeWidths[i % badgeWidths.length]} rounded-full`} />
                  </td>

                  {/* Representative */}
                  <td className="px-6 py-4">
                    <Sk className={`h-4 ${repWidths[i % repWidths.length]} rounded mb-1`} />
                    <Sk className={`h-3 ${emailWidths[i % emailWidths.length]} rounded`} />
                  </td>

                  {/* College/Campus */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sk className={`h-3.5 ${collegeWidths[i % collegeWidths.length]} rounded`} />
                  </td>

                  {/* Completion */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2 mb-1.5">
                      <Sk className="h-3 w-8 rounded" />
                      {i % 2 === 1 && <Sk className="h-3 w-14 rounded-sm" />}
                    </div>
                    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full bg-gray-200 ${progressWidths[i % progressWidths.length]}`} />
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sk className="h-6 w-16 rounded-full" />
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center ml-auto">
                      <Sk className="w-4 h-4 rounded-full" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* Pagination Skeleton */}
    <div className="flex justify-center mt-8">
      <div className="flex items-center space-x-2 bg-white rounded-xl px-3 py-1.5 shadow-sm border border-gray-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-[10px] border-2 border-gray-300 text-gray-300">
          <Icons.ChevronLeft />
        </div>
        <div className="w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] bg-[#125845] text-white border-[3px] border-[#91c3b1] shadow-sm">
          1
        </div>
        <div className="w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] bg-white text-gray-400 border-2 border-gray-300">
          2
        </div>
        <div className="w-9 h-9 flex items-center justify-center rounded-[10px] border-2 border-gray-400 text-gray-400">
          <Icons.ChevronRight />
        </div>
      </div>
    </div>
  </div>
);

// ── Admin Off-Campus Activity Table Skeleton ──────────────────────────────
export const AdminOffCampusSkeleton = ({ rows = 6 }) => (
  <div className="space-y-6 relative pb-16">
    {/* Page Title & Subtitle */}
    <div>
      <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">
        Off-Campus Activity Applications
      </h2>
      <p className="text-gray-500 text-sm mt-1">
        Review and process organization off-campus activity requirements.
      </p>
    </div>

    {/* Controls: Search, Select, Download All (No Filter button) */}
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
      <div className="flex items-center space-x-3 w-full md:w-auto">
        {/* Search input mock */}
        <div className="relative w-full md:w-[400px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Icons.Search />
          </div>
          <div className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 bg-white text-sm font-medium flex items-center h-[42px]">
            <span className="text-gray-400 select-none">Search by organization name...</span>
          </div>
        </div>

        {/* Select button mock */}
        <div className="bg-white border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg text-sm font-medium flex items-center space-x-2 h-[42px] select-none">
          <Icons.Select />
          <span>Select</span>
        </div>
      </div>

      {/* Download All button mock on the right */}
      <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold border-2 border-[#054334] text-[#054334] bg-white h-[40px] select-none">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        <span>Download All</span>
      </div>
    </div>

    {/* Table Card — 5 columns (Activity, Representative, College/Campus, Completion, Actions) — NO Status column */}
    <div className="bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse table-fixed min-w-[700px]">
          <thead>
            <tr className="bg-[#086C53] text-white">
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider rounded-tl-[12px] w-[32%] min-w-[190px]">
                Activity
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[24%] min-w-[160px]">
                Representative
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[16%] min-w-[120px]">
                College/Campus
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[18%] min-w-[140px]">
                Completion
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right rounded-tr-[12px] w-[10%] min-w-[90px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[...Array(rows)].map((_, i) => {
              const actWidths = ['w-3/4', 'w-5/6', 'w-2/3', 'w-4/5', 'w-1/2', 'w-3/5'];
              const repWidths = ['w-28', 'w-36', 'w-32', 'w-24', 'w-30', 'w-34'];
              const emailWidths = ['w-32', 'w-40', 'w-36', 'w-28', 'w-36', 'w-32'];
              const collegeWidths = ['w-10', 'w-12', 'w-14', 'w-10', 'w-12', 'w-10'];
              const progressWidths = ['w-full', 'w-1/4', 'w-4/5', 'w-1/3', 'w-2/3', 'w-1/2'];

              return (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  {/* Activity title (no badges underneath) */}
                  <td className="px-6 py-4">
                    <Sk className={`h-4 ${actWidths[i % actWidths.length]} rounded`} />
                  </td>

                  {/* Representative */}
                  <td className="px-6 py-4">
                    <Sk className={`h-4 ${repWidths[i % repWidths.length]} rounded mb-1`} />
                    <Sk className={`h-3 ${emailWidths[i % emailWidths.length]} rounded`} />
                  </td>

                  {/* College/Campus */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sk className={`h-3.5 ${collegeWidths[i % collegeWidths.length]} rounded`} />
                  </td>

                  {/* Completion */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2 mb-1.5">
                      <Sk className="h-3 w-8 rounded" />
                      {i % 2 === 1 && <Sk className="h-3 w-14 rounded-sm" />}
                    </div>
                    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full bg-gray-200 ${progressWidths[i % progressWidths.length]}`} />
                    </div>
                  </td>

                  {/* Actions — download & cert icons */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-1.5 ml-auto">
                      <Sk className="w-6 h-6 rounded-lg" />
                      <Sk className="w-6 h-6 rounded-lg" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* Pagination Skeleton */}
    <div className="flex justify-center mt-8">
      <div className="flex items-center space-x-2 bg-white rounded-xl px-3 py-1.5 shadow-sm border border-gray-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-[10px] border-2 border-gray-300 text-gray-300">
          <Icons.ChevronLeft />
        </div>
        <div className="w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] bg-[#125845] text-white border-[3px] border-[#91c3b1] shadow-sm">
          1
        </div>
        <div className="w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] bg-white text-gray-400 border-2 border-gray-300">
          2
        </div>
        <div className="w-9 h-9 flex items-center justify-center rounded-[10px] border-2 border-gray-400 text-gray-400">
          <Icons.ChevronRight />
        </div>
      </div>
    </div>
  </div>
);

// ── Admin Organizations Table Skeleton ────────────────────────────────────
export const AdminOrganizationsSkeleton = ({ rows = 6 }) => (
  <div className="space-y-6">
    {/* Page Title */}
    <div className="flex items-center">
      <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">
        All Organizations
      </h2>
      <Sk className="h-6 w-10 rounded-full ml-2" />
    </div>

    {/* Controls: Search, College filter, Download All Files */}
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
      <div className="flex items-center space-x-3 w-full md:w-auto">
        {/* Search input mock */}
        <div className="relative w-full md:w-[400px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Icons.Search />
          </div>
          <div className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 bg-white text-sm font-medium flex items-center h-[42px]">
            <span className="text-gray-400 select-none">Search by organization name...</span>
          </div>
        </div>

        {/* College dropdown mock */}
        <div className="bg-white border border-gray-300 text-gray-700 py-2.5 pl-4 pr-10 rounded-lg text-sm font-medium w-48 flex items-center justify-between h-[42px] select-none">
          <span>College</span>
          <Icons.ChevronDown />
        </div>
      </div>

      {/* Download All Files button mock */}
      <div className="flex items-center gap-3 w-full md:w-auto justify-end">
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold border-2 border-[#054334] text-[#054334] bg-white h-[40px] select-none">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>Download All Files</span>
        </div>
      </div>
    </div>

    {/* Table Card */}
    <div className="bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse table-fixed min-w-[720px]">
          <thead>
            <tr className="bg-[#086C53] text-white">
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider rounded-tl-[12px] w-[26%] min-w-[170px]">
                Representative
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[20%] min-w-[140px]">
                Organization
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[24%] min-w-[160px]">
                Email
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[14%] min-w-[100px]">
                College
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center w-[8%] min-w-[70px]">
                Status
              </th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right rounded-tr-[12px] w-[8%] min-w-[70px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[...Array(rows)].map((_, i) => {
              const repWidths = ['w-36', 'w-24', 'w-44', 'w-32', 'w-28', 'w-40'];
              const orgWidths = ['w-12', 'w-24', 'w-10', 'w-16', 'w-14', 'w-20'];
              const emailWidths = ['w-36', 'w-40', 'w-32', 'w-44', 'w-28', 'w-36'];
              const collegeWidths = ['w-14', 'w-16', 'w-14', 'w-12', 'w-20', 'w-12'];

              return (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  {/* Representative with avatar */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <Sk className="w-8 h-8 rounded-full flex-shrink-0" />
                      <Sk className={`h-4 ${repWidths[i % repWidths.length]} rounded`} />
                    </div>
                  </td>

                  {/* Organization acronym */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sk className={`h-4 ${orgWidths[i % orgWidths.length]} rounded`} />
                  </td>

                  {/* Email */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sk className={`h-3.5 ${emailWidths[i % emailWidths.length]} rounded`} />
                  </td>

                  {/* College */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sk className={`h-3.5 ${collegeWidths[i % collegeWidths.length]} rounded`} />
                  </td>

                  {/* Status dot (centered) */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center">
                      <Sk className="w-2.5 h-2.5 rounded-full" />
                    </div>
                  </td>

                  {/* Actions (3-dots) */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center ml-auto">
                      <Sk className="w-4 h-4 rounded-full" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* Pagination Skeleton */}
    <div className="flex justify-center pb-8 mt-8">
      <div className="flex items-center space-x-2 bg-white rounded-xl px-3 py-1.5 shadow-sm border border-gray-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-[10px] border-2 border-gray-300 text-gray-300">
          <Icons.ChevronLeft />
        </div>
        <div className="w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] bg-[#125845] text-white border-[3px] border-[#91c3b1] shadow-sm">
          1
        </div>
        <div className="w-9 h-9 flex items-center justify-center rounded-[10px] border-2 border-gray-300 text-gray-300">
          <Icons.ChevronRight />
        </div>
      </div>
    </div>
  </div>
);

// ── Access Requests / List Skeleton ───────────────────────────────────────
export const ListSkeleton = ({ rows = 5 }) => (
  <div className="bg-white rounded-[16px] border border-gray-200 overflow-hidden">
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="flex items-center space-x-4 px-6 py-4 border-b border-gray-100 last:border-0">
        <Sk className="h-10 w-10 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Sk className="h-3 w-40" />
          <Sk className="h-2 w-24" />
        </div>
        <Sk className="h-7 w-20 rounded-full" />
        <Sk className="h-7 w-7 rounded-md" />
      </div>
    ))}
  </div>
);

// ── Profile Skeleton ──────────────────────────────────────────────────────
export const ProfileSkeleton = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-[20px] border border-gray-200 p-8 flex items-center space-x-6">
      <Sk className="h-24 w-24 rounded-full flex-shrink-0" />
      <div className="space-y-3 flex-1">
        <Sk className="h-5 w-48" />
        <Sk className="h-3 w-32" />
        <Sk className="h-3 w-56" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-[16px] border border-gray-200 p-5 space-y-3">
          <Sk className="h-2.5 w-24" />
          <Sk className="h-9 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

// ── Announcements Skeleton ────────────────────────────────────────────────
export const AnnouncementsSkeleton = ({ count = 4 }) => (
  <div className="space-y-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white rounded-[16px] border border-gray-200 p-6 space-y-3">
        <div className="flex items-center space-x-3">
          <Sk className="h-9 w-9 rounded-full flex-shrink-0" />
          <div className="space-y-1.5 flex-1">
            <Sk className="h-3 w-32" />
            <Sk className="h-2 w-20" />
          </div>
        </div>
        <Sk className="h-4 w-3/4" />
        <Sk className="h-3 w-full" />
        <Sk className="h-3 w-5/6" />
      </div>
    ))}
  </div>
);

// ── Notifications Skeleton ────────────────────────────────────────────────
export const NotificationsSkeleton = ({ rows = 6 }) => (
  <div className="bg-white rounded-[16px] border border-gray-200 divide-y divide-gray-100">
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="flex items-start space-x-4 px-6 py-4">
        <Sk className="h-9 w-9 rounded-full flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <Sk className="h-3 w-48" />
          <Sk className="h-2 w-64" />
          <Sk className="h-2 w-20" />
        </div>
      </div>
    ))}
  </div>
);

// ── Cards Grid Skeleton ───────────────────────────────────────────────────
export const CardsGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white rounded-[16px] border border-gray-200 p-5 space-y-3">
        <Sk className="h-32 w-full rounded-lg" />
        <Sk className="h-4 w-3/4" />
        <Sk className="h-3 w-1/2" />
      </div>
    ))}
  </div>
);

// ── Requirement Slots Skeleton (for user accreditation & off-campus) ─────────
export const RequirementSlotsSkeleton = ({ count = 8, titleWidth = 'w-64 sm:w-80', subtitle = false }) => (
  <div className="space-y-4 w-full">
    {/* Page Header */}
    <div className="mb-4 space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <Sk className={`h-8 ${titleWidth} rounded-lg`} />
          {subtitle && <Sk className="h-3.5 w-56 rounded mt-2" />}
        </div>
        {/* Org status badge skeleton */}
        <Sk className="h-8 w-44 rounded-xl self-start sm:self-auto flex-shrink-0" />
      </div>
    </div>

    {/* AI Summary Banner */}
    <div className="rounded-[12px] bg-[#f3f0ff] border border-[#e0d9ff] px-5 py-3.5 mb-4 space-y-1.5">
      <Sk className="h-3.5 w-3/4 max-w-xl rounded bg-[#e0d9ff]/70" />
    </div>

    {/* Requirement Slot Cards */}
    <div className="space-y-4">
      {[...Array(count)].map((_, i) => {
        const widths = ['w-36', 'w-64', 'w-44', 'w-48', 'w-52', 'w-40', 'w-36', 'w-60'];
        const titleW = widths[i % widths.length];

        return (
          <div
            key={i}
            className="bg-white rounded-[16px] border border-gray-200 shadow-sm overflow-hidden"
          >
            {/* Card Header */}
            <div className="px-4 sm:px-5 py-3.5 bg-[#6E968C] text-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  {/* Slot Number Badge */}
                  <div className="w-7 h-7 rounded-lg bg-white/15 border border-white/20 flex items-center justify-center font-black text-[12px] text-white/50 flex-shrink-0">

                  </div>
                  {/* Slot Title & Subtitle */}
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <SkLight className={`h-4 ${titleW} rounded`} />
                    <SkLight className="h-2.5 w-14 rounded" />
                  </div>
                </div>

                {/* Upload Action Button */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <div className="w-[72px] h-[30px] bg-white rounded-lg shadow-xs flex items-center justify-center">
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);


